import { useState, useEffect, useRef } from "react";
import { Menu, Globe, Shield, Accessibility, LogIn, LogOut, User, Moon, Sun, Smartphone, X, Info } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { useLanguage } from "../i18n/LanguageContext";
import { useTheme } from "../contexts/ThemeContext";
import { ProtectedPdfButton } from "./ProtectedPdfButton";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LOGO_URL = "/icons/mart-logo-full.jpg";
const NEW_LOGO_URL = "/icons/mart-logo-menu.png";

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [isLoadingAuth, setIsLoadingAuth] = useState(true);
  const [showHighlight, setShowHighlight] = useState(false);
  const [logoClickCount, setLogoClickCount] = useState(0);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [showInstallGuide, setShowInstallGuide] = useState(false);
  const [showHomeScreenTip, setShowHomeScreenTip] = useState(false);
  const [tipClosing, setTipClosing] = useState(false);
  const logoClickTimer = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language, toggleLanguage } = useLanguage();
  const { isDarkMode, toggleDarkMode } = useTheme();
  
  // Detect if iOS
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const isAndroid = /Android/.test(navigator.userAgent);
  
  // PWA Install prompt
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches || 
        window.navigator.standalone === true) {
      setIsInstalled(true);
    }
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);
  
  const handleInstallApp = async () => {
    if (deferredPrompt) {
      // Chrome/Android - use native prompt
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        setIsInstalled(true);
      }
      setDeferredPrompt(null);
    } else {
      // iOS or other - show manual guide
      setShowInstallGuide(true);
    }
  };
  
  // Handle logo clicks - single click goes home, 5 rapid clicks goes to admin
  const handleLogoClick = (e) => {
    e.preventDefault();
    
    // Clear previous timer
    if (logoClickTimer.current) {
      clearTimeout(logoClickTimer.current);
    }
    
    const newCount = logoClickCount + 1;
    setLogoClickCount(newCount);
    
    if (newCount >= 5) {
      // Navigate to admin after 5 rapid clicks
      navigate('/admin');
      setLogoClickCount(0);
    } else {
      // Set timer - if no more clicks within 400ms, go home
      logoClickTimer.current = setTimeout(() => {
        if (logoClickCount < 4) {
          // Single/few clicks - go to home
          navigate('/');
        }
        setLogoClickCount(0);
      }, 400);
    }
  };
  
  // Timer for delayed highlight animation
  useEffect(() => {
    let timer;
    if (isOpen) {
      // Start highlight animation after 7 seconds if menu stays open
      timer = setTimeout(() => {
        setShowHighlight(true);
      }, 7000);
    } else {
      setShowHighlight(false);
      setShowHomeScreenTip(false); // Close tip when menu closes
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [isOpen]);

  // Counter to force re-trigger of tooltip timer
  const [tipTriggerCount, setTipTriggerCount] = useState(0);

  // Auto-close tooltip after 6 seconds with fade-out animation
  useEffect(() => {
    let tipTimer;
    let closeTimer;
    if (showHomeScreenTip && !tipClosing) {
      tipTimer = setTimeout(() => {
        setTipClosing(true);
        closeTimer = setTimeout(() => {
          setShowHomeScreenTip(false);
          setTipClosing(false);
        }, 500); // Wait for animation to complete
      }, 6000);
    }
    return () => {
      if (tipTimer) clearTimeout(tipTimer);
      if (closeTimer) clearTimeout(closeTimer);
    };
  }, [showHomeScreenTip, tipClosing, tipTriggerCount]);

  // Function to show/reset tip - always shows and resets timer
  const handleTipLogoClick = () => {
    if (tipClosing) return; // Don't interrupt closing animation
    
    if (showHomeScreenTip) {
      // Already showing - reset the timer by incrementing trigger count
      setTipTriggerCount(c => c + 1);
    } else {
      // Not showing - show it
      setShowHomeScreenTip(true);
    }
  };

  // Function to close tip with animation
  const closeTipWithAnimation = () => {
    setTipClosing(true);
    setTimeout(() => {
      setShowHomeScreenTip(false);
      setTipClosing(false);
    }, 500);
  };

  // Preload menu logo for instant display
  useEffect(() => {
    const img = new Image();
    img.src = NEW_LOGO_URL;
  }, []);

  // Handle mobile back button - close menu instead of leaving site
  useEffect(() => {
    if (isOpen) {
      // Push a dummy state when menu opens
      window.history.pushState({ menuOpen: true }, '');
      
      const handlePopState = (event) => {
        if (isOpen) {
          setIsOpen(false);
          // Prevent default back navigation
          event.preventDefault();
        }
      };
      
      window.addEventListener('popstate', handlePopState);
      
      return () => {
        window.removeEventListener('popstate', handlePopState);
      };
    }
  }, [isOpen]);

  // Check auth status
  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Get token from localStorage (for cross-domain support)
        const storedToken = localStorage.getItem('mart_session_token');
        
        const headers = {};
        if (storedToken) {
          headers['Authorization'] = `Bearer ${storedToken}`;
        }
        
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: "include",
          headers,
        });
        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
        } else {
          // Clear invalid token
          localStorage.removeItem('mart_session_token');
        }
      } catch (error) {
        console.log("Not authenticated");
      } finally {
        setIsLoadingAuth(false);
      }
    };
    checkAuth();
  }, [location.pathname]); // Re-check on route change

  const handleLogin = () => {
    // Save current page to return to after login
    localStorage.setItem('mart_auth_redirect', window.location.pathname);
    const redirectUrl = window.location.origin + window.location.pathname;
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleLogout = async () => {
    try {
      const storedToken = localStorage.getItem('mart_session_token');
      const headers = {};
      if (storedToken) {
        headers['Authorization'] = `Bearer ${storedToken}`;
      }
      
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
        headers,
      });
    } catch (error) {
      console.error("Logout error:", error);
    }
    // Clear localStorage token
    localStorage.removeItem('mart_session_token');
    setUser(null);
    setIsOpen(false);
  };

  const navItems = [
    { href: "/", label: t.nav.home },
    { href: "/diy", label: language === 'he' ? 'להכין דאודורנט בעצמך' : 'Make Your Own Deodorant', badge: language === 'he' ? 'בחינם' : 'Free', highlight: true },
    { href: "/diy-kit", label: language === 'he' ? 'ערכה להכנה עצמית' : 'DIY Kit', badge: language === 'he' ? 'חדש!' : 'New!' },
    { href: "/calculator", label: language === 'he' ? 'מחשבון חומרים' : 'Calculator' },
    { href: "/product", label: t.nav.product },
    { href: "/community", label: t.nav.community },
    { href: "/why-open", label: t.nav.whyOpen },
    { href: "/articles", label: language === 'he' ? 'מאמרים' : 'Articles' },
    { href: "/about", label: language === 'he' ? 'אודות' : 'About' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 right-0 left-0 z-40 transition-all duration-500 ${
        scrolled 
          ? "bg-[#FAF8F5]/95 backdrop-blur-md border-b border-[#E8E4DC] shadow-sm" 
          : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile Header */}
        <div className="flex lg:hidden items-center justify-between h-20">
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                data-testid="mobile-menu-btn"
                className="menu-btn-glow hover:bg-[#4A5D43]/10 w-12 h-12 rounded-xl border border-[#4A5D43]/20 bg-[#FAF8F5]/80 backdrop-blur-sm"
              >
                <Menu className="h-6 w-6 text-[#4A5D43]" />
              </Button>
            </SheetTrigger>
            <SheetContent 
              side={language === 'he' ? "right" : "left"} 
              className="w-[85vw] max-w-sm !bg-[#FAF8F5] dark:!bg-[#121812] border-[#E8E4DC] dark:border-[#2a3a2a] p-0 flex flex-col h-full overflow-hidden [&>button]:text-[#2C3329] [&>button]:dark:text-white"
              onOpenAutoFocus={(e) => e.preventDefault()}
              onInteractOutside={() => setIsOpen(false)}
              onEscapeKeyDown={() => setIsOpen(false)}
            >
              {/* Header with Logo & Close Button */}
              <div className="flex items-center justify-between px-4 pt-4 pb-3 border-b border-[#E8E4DC] dark:border-[#2a3a2a] bg-[#FAF8F5] dark:bg-[#121812]">
                <div className="flex items-center gap-2">
                  <img src={LOGO_URL} alt="M'ART" className="h-9 w-9 rounded-full border-2 border-[#E8E4DC] dark:border-[#2a3a2a] shadow-sm" />
                </div>
                
                {/* Center Logo - Clickable for tip */}
                <div className="relative">
                  <button
                    onClick={handleTipLogoClick}
                    className="focus:outline-none transform hover:scale-105 transition-all duration-300 group"
                    aria-label={language === 'he' ? 'טיפ להוספה למסך הבית' : 'Add to home screen tip'}
                  >
                    <div className="relative">
                      <img 
                        src={NEW_LOGO_URL} 
                        alt="M'ART" 
                        className={`h-14 w-14 rounded-xl shadow-lg object-cover transition-all duration-300 ${
                          !showHomeScreenTip ? 'animate-pulse-subtle ring-2 ring-[#4A5D43]/30 ring-offset-2 ring-offset-[#FAF8F5] dark:ring-offset-[#121812]' : ''
                        }`}
                        style={{ background: 'transparent' }}
                      />
                      {/* Tap hint - shows when tooltip is closed */}
                      {!showHomeScreenTip && (
                        <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-[#4A5D43] rounded-full flex items-center justify-center shadow-md animate-bounce-subtle">
                          <span className="text-white text-[10px]">💡</span>
                        </div>
                      )}
                    </div>
                  </button>
                  
                  {/* Home Screen Tip Tooltip */}
                  {showHomeScreenTip && (
                    <div 
                      className={`absolute top-full left-1/2 -translate-x-1/2 mt-3 w-64 z-50 transition-all duration-500 ease-out ${
                        tipClosing 
                          ? 'opacity-0 translate-y-2 scale-95' 
                          : 'opacity-100 translate-y-0 scale-100 animate-in fade-in slide-in-from-top-2'
                      }`}
                    >
                      <div className="bg-white dark:bg-[#1a231a] rounded-xl shadow-xl border border-[#E8E4DC] dark:border-[#2a3a2a] p-4 relative overflow-hidden">
                        {/* Arrow */}
                        <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white dark:bg-[#1a231a] border-t border-l border-[#E8E4DC] dark:border-[#2a3a2a] transform rotate-45"></div>
                        
                        {/* Progress bar for auto-close - key resets animation on each trigger */}
                        <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#E8E4DC] dark:bg-[#2a3a2a] overflow-hidden">
                          <div 
                            key={tipTriggerCount}
                            className="h-full bg-gradient-to-r from-[#4A5D43] to-[#7DAA6B] animate-shrink-width"
                            style={{ animationDuration: '6s', animationTimingFunction: 'linear' }}
                          />
                        </div>
                        
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gradient-to-br from-[#4A5D43]/20 to-[#7DAA6B]/20 flex items-center justify-center">
                            <Smartphone className="h-4 w-4 text-[#4A5D43]" />
                          </div>
                          <div className="flex-1">
                            <p className="text-sm font-semibold text-[#2C3329] dark:text-white mb-1">
                              {language === 'he' ? 'שתדעו 💡' : 'Did you know? 💡'}
                            </p>
                            <p className="text-xs text-[#6B7266] dark:text-[#9A9790] leading-relaxed">
                              {language === 'he' 
                                ? 'לחצו על ⋮ (3 נקודות) בדפדפן ובחרו "הוסף למסך הבית" לגישה מהירה לאתר!' 
                                : 'Click ⋮ (3 dots) in your browser and select "Add to Home Screen" for quick access!'}
                            </p>
                          </div>
                        </div>
                        
                        {/* Close button */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            closeTipWithAnimation();
                          }}
                          className="absolute top-2 left-2 w-5 h-5 rounded-full bg-[#E8E4DC] dark:bg-[#2a3a2a] flex items-center justify-center hover:bg-[#ddd] dark:hover:bg-[#3a4a3a] transition-colors"
                        >
                          <X className="h-3 w-3 text-[#6B7266]" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                
                {/* Close Button - Prominent */}
                <button
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-menu-close"
                  className="flex items-center justify-center w-10 h-10 rounded-xl bg-[#2C3329] dark:bg-[#1a231a] text-white shadow-md hover:bg-[#1a1f18] dark:hover:bg-[#0f1510] transition-all hover:scale-105 active:scale-95"
                  aria-label={language === 'he' ? 'סגור תפריט' : 'Close menu'}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              
              {/* User Section - Compact */}
              <div className="px-4 py-3 bg-[#FAF8F5] dark:bg-[#1a231a] border-b border-[#E8E4DC] dark:border-[#2a3a2a]">
                {!isLoadingAuth && (
                  user ? (
                    <div className="flex items-center gap-2 p-2 bg-white dark:bg-[#121812] rounded-xl border border-[#E8E4DC] dark:border-[#2a3a2a] shadow-sm">
                      <Avatar className="h-9 w-9 border border-[#4A5D43]/20">
                        <AvatarImage src={user.picture} alt={user.name} />
                        <AvatarFallback className="bg-[#4A5D43]/10 text-xs">
                          {user.name?.charAt(0) || <User className="h-4 w-4 text-[#4A5D43]" />}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-[#2C3329] dark:text-white text-sm truncate">{user.name}</p>
                        <p className="text-[10px] text-[#6B7266] dark:text-[#9A9790] truncate">{user.email}</p>
                      </div>
                      <button
                        onClick={handleLogout}
                        data-testid="mobile-logout-btn"
                        className="p-1.5 text-[#C47F64] hover:bg-[#C47F64]/10 rounded-lg transition-all"
                        title={language === 'he' ? 'התנתקות' : 'Log Out'}
                      >
                        <LogOut className="h-4 w-4" />
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={handleLogin}
                      data-testid="mobile-login-btn"
                      className="flex items-center justify-center gap-2 w-full px-3 py-3 bg-white dark:bg-[#121812] border border-[#E8E4DC] dark:border-[#2a3a2a] text-[#2C3329] dark:text-white rounded-xl shadow-sm hover:shadow-md transition-all text-sm font-semibold"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24">
                        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                      {language === 'he' ? 'התחברות' : 'Sign in'}
                    </button>
                  )
                )}
              </div>

              {/* Navigation Links - Compact */}
              <nav className="flex-1 px-4 py-3 overflow-y-auto bg-[#FAF8F5] dark:bg-[#0f1510]" style={{ backgroundColor: 'var(--tw-bg-opacity, 1) #FAF8F5' }}>
                <div className="rounded-xl overflow-hidden border border-[#E8E4DC] dark:border-[#2a3a2a] shadow-sm bg-[#FAF8F5] dark:bg-[#121812]">
                  {navItems.map((item, index) => (
                    <Link
                      key={item.href}
                      to={item.href}
                      data-testid={`mobile-nav-${item.href.replace("/", "") || "home"}`}
                      onClick={() => setIsOpen(false)}
                      className={`block px-4 py-3.5 text-sm transition-all ${
                        index !== navItems.length - 1 ? 'border-b border-[#E8E4DC] dark:border-[#2a3a2a]' : ''
                      } ${
                        location.pathname === item.href
                          ? "bg-[#E8E4DC] dark:bg-[#4A5D43]/30 font-bold"
                          : "bg-[#FAF8F5] dark:bg-[#121812] font-medium hover:bg-[#E8E4DC] dark:hover:bg-[#1a231a]"
                      } ${item.highlight && showHighlight ? 'menu-item-highlight' : ''}`}
                      style={{ 
                        color: location.pathname === item.href ? '#4A5D43' : '#2C3329',
                        backgroundColor: location.pathname === item.href ? '#E8E4DC' : '#FAF8F5'
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-[#2C3329] dark:text-[#E8E4DC]" style={{ color: '#2C3329' }}>{item.label}</span>
                        {item.badge && (
                          <span className="text-[9px] font-bold px-2 py-0.5 rounded-full bg-gradient-to-r from-[#4A5D43] to-[#5D7356] text-white">
                            {item.badge}
                          </span>
                        )}
                      </div>
                    </Link>
                  ))}
                </div>
                
                {/* Quick Actions - Grid Layout with Labels */}
                <div className="mt-4 grid grid-cols-4 gap-2">
                  {/* Dark Mode */}
                  <button
                    onClick={toggleDarkMode}
                    data-testid="dark-mode-toggle"
                    className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-[#2C3329] dark:bg-[#1a231a] shadow-md transition-all hover:scale-105 active:scale-95"
                  >
                    {isDarkMode ? (
                      <Sun className="h-5 w-5 text-amber-300" />
                    ) : (
                      <Moon className="h-5 w-5 text-indigo-200" />
                    )}
                    <span className="text-[9px] text-white font-medium">
                      {isDarkMode ? (language === 'he' ? 'בהיר' : 'Light') : (language === 'he' ? 'כהה' : 'Dark')}
                    </span>
                  </button>
                  
                  {/* Accessibility */}
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      const accessibilityBtn = document.querySelector('[data-testid="accessibility-toggle"]');
                      if (accessibilityBtn) accessibilityBtn.click();
                    }}
                    className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-white dark:bg-[#1a231a] border border-[#E8E4DC] dark:border-[#2a3a2a] shadow-sm transition-all hover:bg-[#F5F2EB] dark:hover:bg-[#2a3a2a] active:scale-95"
                  >
                    <Accessibility className="h-5 w-5 text-[#4A5D43] dark:text-[#7DAA6B]" />
                    <span className="text-[9px] text-[#2C3329] dark:text-[#E8E4DC] font-medium">
                      {language === 'he' ? 'נגישות' : 'Access'}
                    </span>
                  </button>
                  
                  {/* Terms */}
                  <Link
                    to="/terms"
                    onClick={() => setIsOpen(false)}
                    className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-white dark:bg-[#1a231a] border border-[#E8E4DC] dark:border-[#2a3a2a] shadow-sm transition-all hover:bg-[#F5F2EB] dark:hover:bg-[#2a3a2a] active:scale-95"
                  >
                    <Shield className="h-5 w-5 text-[#4A5D43] dark:text-[#7DAA6B]" />
                    <span className="text-[9px] text-[#2C3329] dark:text-[#E8E4DC] font-medium">
                      {language === 'he' ? 'תקנון' : 'Terms'}
                    </span>
                  </Link>
                  
                  {/* PDF - Protected */}
                  <div className="flex flex-col items-center justify-center gap-1 py-3 rounded-xl bg-[#C47F64] shadow-md">
                    <ProtectedPdfButton variant="navbar-mobile" language={language} />
                  </div>
                </div>
                
                {/* Install App - Coming Soon */}
                <div className="mt-3 relative">
                  <button
                    disabled
                    data-testid="install-app-btn"
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 bg-white dark:bg-[#1a231a] text-[#6B7266] dark:text-[#666] rounded-xl text-sm font-medium cursor-not-allowed border border-[#E8E4DC] dark:border-[#2a3a2a]"
                  >
                    <Smartphone className="h-4 w-4" />
                    {language === 'he' ? 'התקן אפליקציה' : 'Install App'}
                  </button>
                  <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 px-2 py-0.5 bg-amber-500 text-white text-[9px] font-bold rounded-full shadow-sm">
                    {language === 'he' ? 'בקרוב' : 'Soon'}
                  </div>
                </div>
              </nav>
            </SheetContent>
          </Sheet>
          
          {/* Mobile Logo - Centered and larger with background */}
          <div 
            onClick={handleLogoClick}
            data-testid="logo-link" 
            className="absolute left-1/2 -translate-x-1/2 cursor-pointer"
          >
            <div className="relative">
              <div className="absolute inset-0 bg-[#FAF8F5] rounded-full scale-110 -z-10" />
              <img
                src={LOGO_URL}
                alt="M'ART"
                className="h-14 w-14 object-cover rounded-full border-2 border-[#E8E4DC] shadow-md"
              />
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={toggleLanguage}
            data-testid="language-toggle"
            className="flex items-center gap-1.5 text-xs font-medium hover:bg-[#E8E4DC]/50"
          >
            <Globe className="h-3.5 w-3.5" />
            <span>{language === 'he' ? 'EN' : 'עב'}</span>
          </Button>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between h-20">
          {/* Left side: Language Toggle & Dark Mode */}
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              data-testid="language-toggle-desktop"
              className="flex items-center gap-1.5 text-xs font-medium hover:bg-[#E8E4DC]/50 dark:hover:bg-[#2a3a2a]/50"
            >
              <Globe className="h-3.5 w-3.5" />
              <span>{language === 'he' ? 'EN' : 'עב'}</span>
            </Button>
            
            {/* Desktop Dark Mode Toggle */}
            <button
              onClick={toggleDarkMode}
              data-testid="dark-mode-toggle-desktop"
              className="group relative w-9 h-9 flex items-center justify-center rounded-full bg-gradient-to-br from-[#2C3329] to-[#3D4A38] dark:from-[#1a231a] dark:to-[#2a3a2a] shadow-md hover:shadow-lg transition-all duration-300 hover:scale-105 overflow-hidden"
              aria-label={isDarkMode ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {/* Moon icon */}
              <div className={`absolute transition-all duration-500 ease-out ${isDarkMode ? 'opacity-0 rotate-90 scale-50' : 'opacity-100 rotate-0 scale-100'}`}>
                <Moon className="h-4 w-4 text-indigo-200" />
              </div>
              
              {/* Sun icon */}
              <div className={`absolute transition-all duration-500 ease-out ${isDarkMode ? 'opacity-100 rotate-0 scale-100' : 'opacity-0 -rotate-90 scale-50'}`}>
                <Sun className="h-4 w-4 text-amber-300" />
              </div>
            </button>
          </div>

          {/* Center: Navigation Links */}
          <div className="flex items-center gap-1 xl:gap-2">
            {navItems.slice(1).map((item) => (
              <Link
                key={item.href}
                to={item.href}
                data-testid={`nav-${item.href.replace("/", "") || "home"}`}
                className={`relative text-[11px] xl:text-xs tracking-wide transition-all duration-300 whitespace-nowrap flex items-center gap-1 px-2 py-1.5 rounded-full ${
                  item.highlight 
                    ? 'bg-[#4A5D43]/10 border border-[#4A5D43]/20 hover:bg-[#4A5D43]/20' 
                    : 'hover:bg-[#E8E4DC]/50 dark:hover:bg-[#2a3a2a]/50'
                } ${
                  location.pathname === item.href
                    ? "text-[#4A5D43] font-semibold"
                    : "text-[#2C3329]/80 dark:text-[#E8E4DC]/80 hover:text-[#4A5D43]"
                }`}
              >
                {item.label}
                {item.badge && (
                  <span className="text-[8px] font-bold px-1.5 py-0.5 bg-[#4A5D43] text-white rounded-full">
                    {item.badge}
                  </span>
                )}
              </Link>
            ))}
          </div>

          {/* Right side: User/Login, Terms, PDF, Logo */}
          <div className="flex items-center gap-3">
            {/* User section for desktop */}
            {!isLoadingAuth && (
              user ? (
                <div className="flex items-center gap-2 px-2 py-1 rounded-full bg-[#E8E4DC]/50 dark:bg-[#2a3a2a]/50 hover:bg-[#E8E4DC] dark:hover:bg-[#2a3a2a] transition-colors">
                  <Avatar className="h-7 w-7 border border-[#4A5D43]/20">
                    <AvatarImage src={user.picture} alt={user.name} />
                    <AvatarFallback className="bg-[#4A5D43]/10 text-xs">
                      {user.name?.charAt(0) || <User className="h-3 w-3 text-[#4A5D43]" />}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs font-medium text-[#2C3329] dark:text-white max-w-20 truncate">{user.name?.split(' ')[0]}</span>
                  <button
                    onClick={handleLogout}
                    data-testid="desktop-logout-btn"
                    className="p-1 text-[#6B7266] hover:text-[#C47F64] rounded transition-colors"
                    title={language === 'he' ? 'התנתקות' : 'Log Out'}
                  >
                    <LogOut className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={handleLogin}
                  data-testid="desktop-login-btn"
                  className="flex items-center gap-2 px-3 py-1.5 bg-white dark:bg-[#1a231a] border border-[#E8E4DC] dark:border-[#2a3a2a] text-[#2C3329] dark:text-white rounded-full shadow-sm hover:shadow-md transition-all text-xs font-medium"
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  {language === 'he' ? 'התחברות' : 'Login'}
                </button>
              )
            )}
            <Link
              to="/terms"
              data-testid="nav-terms"
              className={`text-xs text-[#6B7266] dark:text-[#9A9790] hover:text-[#4A5D43] transition-colors ${
                location.pathname === "/terms" ? "text-[#4A5D43] font-medium" : ""
              }`}
            >
              {language === 'he' ? 'תקנון' : 'Terms'}
            </Link>
            <ProtectedPdfButton 
              variant="icon-only" 
              language={language}
              className="text-[#C47F64] hover:text-[#A66B52]"
            />
            <div 
              onClick={handleLogoClick}
              data-testid="logo-link-desktop"
              className="cursor-pointer"
            >
              <img
                src={LOGO_URL}
                alt="M'ART"
                className="h-10 w-10 object-cover rounded-full border-2 border-[#E8E4DC] dark:border-[#2a3a2a] shadow-sm hover:shadow-md transition-shadow"
              />
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
